import React from 'react'
import {
    Field,
    FastField,
    FieldArray,
    FieldArrayConfig,
    useField,
    FieldArrayRenderProps
} from 'formik'
import { Input, Ul, Li, FormGroup } from '@bootstrap-styled/v4'

// app components
import AddOrRemove from './AddOrRemove'

// types definition
export interface AddFieldProps extends FieldArrayConfig {
    fieldComp?: typeof Field | typeof FastField
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FieldEntriesProps<V = any> = {
    fieldT: typeof Field | typeof FastField
    entries: V
} & Pick<FieldArrayRenderProps, 'name' | 'push' | 'remove'>

/**
 * render function which it can be override
 * @param {FieldEntriesProps} set options map
 */
const renderFieldEntries = <T extends FieldEntriesProps>({
    name,
    push,
    remove,
    entries,
    fieldT: FieldT
}: T): JSX.Element[] => {
    return entries.map((entry, index) => (
        <FormGroup tag={Ul} className="inline-list-grid" key={`${name}-${index}`}>
            {typeof entry === 'object' && entry !== null ? (
                Object.keys(entry).map(k => (
                    <Li key={`${name}-${index}-${k}`}>
                        <FieldT
                            as={Input}
                            name={`${name}[${index}].${k}`}
                            type="text"
                            placeholder="-"
                        />
                    </Li>
                ))
            ) : (
                <Li>
                    <FieldT as={Input} name={`${name}[${index}]`} type="text" placeholder="-" />
                </Li>
            )}
            <Li className="short-col">
                // Revisar implementación
                <AddOrRemove
                    itemModel=''
                    name={name}
                    index={index}
                    actions={{ push, remove }}
                    toRemove={index < entries.length - 1}
                />
            </Li>
        </FormGroup>
    ))
}

/**
 * Wrapper for manage the item's addition and removal,
 * allow injects custom entries, thus override the current default
 * @param {AddFieldProps} Props
 * @return {React.ReactElement}
 */
const AddField: React.FC<AddFieldProps> = ({
    name,
    children,
    fieldComp: FieldT = Field,
    ...props
}) => {
    const [field, meta] = useField(name)

    return (
        <FieldArray name={name} {...props}>
            {children ||
                (({ remove, push }) =>
                    renderFieldEntries<FieldEntriesProps<typeof meta.initialValue>>({
                        name,
                        push,
                        remove,
                        entries: field.value,
                        fieldT: FieldT
                    }))}
        </FieldArray>
    )
}

export default AddField
